import React, { useState } from 'react';
import axios from '../api/client';
import { purchaseStock } from '../api/private';
import { useUser } from '../context/UserContext';

const inputStyle = {
  height: 40,
  padding: '0 12px',
  borderRadius: 12,
  border: '1px solid rgba(20, 20, 40, 0.12)',
  fontSize: 14,
  outline: 'none',
  background: 'rgba(250, 250, 255, 0.85)',
  transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
};

const buttonBase = {
  flex: 1,
  padding: '11px 12px',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'transform 0.15s, box-shadow 0.15s, opacity 0.15s',
};

const StockPurchase = () => {
  const { user, isLoggedIn } = useUser();
  const [ticker, setTicker] = useState('');
  const [shares, setShares] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

  const handleTickerChange = (value) => {
    setTicker(value.replace(/[^a-zA-Z.\-]/g, '').toUpperCase());
  };

  const fetchLatestPrice = async () => {
    const cleanTicker = ticker.trim().toUpperCase();
    if (!cleanTicker) return null;

    setIsFetchingPrice(true);
    try {
      const proxyUrl = 'https://stocksleuth.xyz:5000/call?url=';
      const apiUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${cleanTicker}?metrics=regularMarketPrice&interval=1d&range=1d`;
      const response = await axios.get(proxyUrl + encodeURIComponent(apiUrl));
      const quote = response.data.chart?.result?.[0]?.indicators?.quote?.[0];
      const latestClose = quote?.close?.filter(Boolean)?.slice(-1)?.[0];

      if (!latestClose) throw new Error('No latest price returned');

      const formattedPrice = parseFloat(latestClose).toFixed(2);
      setPurchasePrice(formattedPrice);
      return formattedPrice;
    } catch (error) {
      console.error('Failed to fetch price', error);
      setMessage('Could not fetch the latest price. You can enter it manually.');
      return null;
    } finally {
      setIsFetchingPrice(false);
    }
  };

  const handlePurchase = async (type) => {
    if (isSubmitting) return;

    const cleanTicker = ticker.trim().toUpperCase();
    const parsedShares = Number(shares);
    let finalPrice = purchasePrice ? Number(purchasePrice) : null;

    if (!cleanTicker || !parsedShares || parsedShares <= 0) {
      setMessage('Add a ticker and a positive share count.');
      return;
    }

    if (!finalPrice) {
      const fetchedPrice = await fetchLatestPrice();
      finalPrice = fetchedPrice ? Number(fetchedPrice) : null;
    }

    if (!finalPrice || finalPrice <= 0) {
      setMessage('Add a valid price before submitting.');
      return;
    }

    setIsSubmitting(true);
    setMessage('Submitting order…');

    try {
      const datetime = new Date(`${selectedDate} ${selectedTime}`).toISOString();
      await purchaseStock({
        ticker: cleanTicker,
        shares: type === 'sell' ? -Math.abs(parsedShares) : Math.abs(parsedShares),
        purchasePrice: finalPrice,
        datetime,
      });
      setMessage('Order submitted.');
      window.location.reload();
    } catch (error) {
      console.error('Purchase failed', error);
      setMessage('Order failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isLoggedIn) return <p style={{ textAlign: 'center', marginTop: 50 }}>Please log in to place orders.</p>;

  const fields = [
    { label: 'Ticker', type: 'text', value: ticker, onChange: (e) => handleTickerChange(e.target.value), onBlur: fetchLatestPrice, placeholder: 'AAPL' },
    { label: 'Shares', type: 'number', value: shares, onChange: (e) => setShares(e.target.value), placeholder: '27', min: '0', step: 'any' },
    { label: isFetchingPrice ? 'Price · fetching…' : 'Price', type: 'number', value: purchasePrice, onChange: (e) => setPurchasePrice(e.target.value), placeholder: '157.92', min: '0', step: '0.01' },
    { label: 'Date', type: 'date', value: selectedDate, onChange: (e) => setSelectedDate(e.target.value) },
    { label: 'Time', type: 'time', value: selectedTime, onChange: (e) => setSelectedTime(e.target.value) },
  ];

  return (
    <div className="stockPurchasePage" style={{ width: '100%' }}>
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}>
        <div style={{ marginBottom: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#5932ea' }}>
            Portfolio order
          </div>
          <h2 style={{ margin: '4px 0 0', fontWeight: 700, fontSize: 24, color: '#151521' }}>Add a trade</h2>
          <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.45, color: 'rgba(0,0,0,0.55)' }}>
            Record manual buys or sells. The price auto-fills when available, but you can overwrite it.
          </p>
        </div>

        {message && (
          <div style={{
            padding: '10px 12px',
            borderRadius: 12,
            fontSize: 12,
            background: message.includes('failed') || message.includes('Could not') || message.includes('valid') || message.includes('positive')
              ? 'rgba(240, 110, 110, 0.12)'
              : 'rgba(89, 50, 234, 0.09)',
            color: message.includes('failed') || message.includes('Could not') || message.includes('valid') || message.includes('positive')
              ? '#b84141'
              : '#3a2499',
          }}>
            {message}
          </div>
        )}

        <div className="stockPurchaseFields">
        {fields.map(({ label, ...props }, idx) => (
          <div className={`stockPurchaseField stockPurchaseField${idx}`} key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'rgba(0,0,0,0.68)' }}>{label}</label>
            <input
              {...props}
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = '#806cf5';
                e.target.style.boxShadow = '0 0 0 4px rgba(89, 50, 234, 0.08)';
                e.target.style.background = '#fff';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(20, 20, 40, 0.12)';
                e.target.style.boxShadow = 'none';
                e.target.style.background = 'rgba(250, 250, 255, 0.85)';
                props.onBlur?.(e);
              }}
            />
          </div>
        ))}
        </div>

        <div className="stockPurchaseActions" style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button
            disabled={isSubmitting}
            onClick={() => handlePurchase('sell')}
            style={{
              ...buttonBase,
              background: 'linear-gradient(135deg, #ff6b6b, #f03e3e)',
              opacity: isSubmitting ? 0.55 : 1,
              boxShadow: '0 10px 20px rgba(240, 62, 62, 0.18)',
            }}
          >
            Sell
          </button>
          <button
            disabled={isSubmitting}
            onClick={() => handlePurchase('buy')}
            style={{
              ...buttonBase,
              background: 'linear-gradient(135deg, #24c178, #159957)',
              opacity: isSubmitting ? 0.55 : 1,
              boxShadow: '0 10px 20px rgba(21, 153, 87, 0.18)',
            }}
          >
            Buy
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockPurchase;
